import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PlanType = 'Free' | 'Basic' | 'Pro';

export interface UserStats {
  totalSales: number;
  activeLeads: number;
  completedDeals: number;
  totalProducts: number;
  activeUsers: number;
}

export interface UserData {
  name: string;
  email?: string;
  avatar?: string | null;
  role?: string;
  company?: string;
  phone?: string;
  plan: PlanType;
  trialStartDate?: string; 
  trialEndDate?: string;   
  currentCycleEnd?: string;
  planFeatures: {
    maxLeads: number;
    maxProducts: number;
    whatsappBot: boolean;
    emailMarketing: boolean;
    chatIA: boolean;
    multipleUsers: boolean;
    customReports: boolean;
    support: 'basic' | 'priority' | '24/7';
    isTrial: boolean;      
    trialDays: number;     
    price: number;         
    description: string;   
  };
  stats: UserStats;
  notes: {
    text: string;
    createdAt: string;
  }[];
  interactions: {
    type: 'email' | 'whatsapp' | 'phone' | 'chat';
    date: string;
    description: string;
    leadId?: string;
  }[];
  createdAt?: any;  
  updatedAt?: any;  
}

interface UserState {
  data: UserData;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const getPlanFeatures = (plan: PlanType) => {
  switch (plan) {
    case 'Free':
      return {
        maxLeads: 50,         
        maxProducts: 10,      
        whatsappBot: true,    
        emailMarketing: true, 
        chatIA: true,         
        multipleUsers: false, 
        customReports: false, 
        support: 'basic' as const,
        isTrial: true,
        trialDays: 15,        
        price: 0,
        description: 'Plano gratuito com 15 dias de acesso completo'
      };
    case 'Basic':
      return {
        maxLeads: 200,
        maxProducts: 50,
        whatsappBot: true,
        emailMarketing: true,
        chatIA: false,
        multipleUsers: false,
        customReports: true,
        support: 'priority' as const,
        isTrial: false,
        trialDays: 0,
        price: 59.90,
        description: 'Plano básico para pequenos negócios'
      };
    case 'Pro':
      return {
        maxLeads: -1,         
        maxProducts: -1,      
        whatsappBot: true,
        emailMarketing: true,
        chatIA: true,
        multipleUsers: true,
        customReports: true,
        support: '24/7' as const,
        isTrial: false,
        trialDays: 0,
        price: 99.90,
        description: 'Plano profissional com recursos avançados'
      };
  }
};

const calculateTrialEndDate = (startDate: string, trialDays: number): string => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + trialDays);
  return endDate.toISOString();
};

const initialState: UserState = {
  data: {
    name: 'João Silva',
    email: 'joao.silva@email.com',
    avatar: null,
    role: 'Vendedor',
    company: 'Empresa ABC',
    phone: '(11) 98765-4321',
    plan: 'Free',
    trialStartDate: new Date().toISOString(),
    trialEndDate: calculateTrialEndDate(new Date().toISOString(), 15),
    currentCycleEnd: calculateTrialEndDate(new Date().toISOString(), 15),
    planFeatures: getPlanFeatures('Free'),
    stats: {
      totalSales: 150,
      activeLeads: 45,
      completedDeals: 98,
      totalProducts: 0,
      activeUsers: 0,
    },
    notes: [],
    interactions: []
  },
  isAuthenticated: true,
  loading: false,
  error: null
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateUserData: (state, action: PayloadAction<Partial<UserData>>) => {
      if (action.payload.plan && action.payload.plan !== state.data.plan) {
        const features = getPlanFeatures(action.payload.plan);
        state.data.planFeatures = features;
        
        if (action.payload.plan === 'Free') {
          const startDate = new Date().toISOString();
          state.data.trialStartDate = startDate;
          state.data.trialEndDate = calculateTrialEndDate(startDate, features.trialDays);
          state.data.currentCycleEnd = calculateTrialEndDate(startDate, features.trialDays);
        } else {
          state.data.trialStartDate = undefined;
          state.data.trialEndDate = undefined;
          state.data.currentCycleEnd = undefined;
        }
      }
      state.data = {
        ...state.data,
        ...action.payload
      };
    },
    setUserAvatar: (state, action: PayloadAction<string>) => {
      state.data.avatar = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  }
});

export const {
  updateUserData,
  setUserAvatar,
  setLoading,
  setError,
  clearError
} = userSlice.actions;

export default userSlice.reducer; 